// src/services/unifiedProductService.js
// Unified Product Management Service
// Consolidates product metadata operations across the application
// Provides single source of truth for product names, SKUs, images, and box definitions

import { getOverriddenProductCategoryIfAny } from '../utils/sausageNotBaconSkuOverrides.js';

import { db } from './firebase';
import {
    collection,
    doc,
    query,
    where,
    getDocs,
    getDoc,
    onSnapshot,
} from 'firebase/firestore';

// ============================================================================
// CONSTANTS
// ============================================================================

export const PRODUCT_COLLECTIONS = {
    PRODUCTS_MASTER: 'products_master',      // Future: centralized product catalog
    PRODUCTS_DEFAULT: 'products_default',    // Current: default pricing tier
    PRODUCTS_PREFIX: 'products_',            // Collection prefix for tiers
    SKU_METADATA: 'SkuMetadata',             // Reference metadata for all SKUs
    BOX_DEFINITIONS: 'BoxDefinitions',       // Box product definitions
    STANDARD_ITEMS: 'StandardItems',         // GTIN to SKU mappings
};

export const SKU_PATTERNS = {
    // For intelligent root extraction: CH-NDSM-0080-SLC → CH-NDSM
    TWO_SEGMENT: /^([A-Z]{2}-[A-Z]{4})-/,
    // Alternative patterns for edge cases
    THREE_SEGMENT: /^([A-Z]{2}-[A-Z]{4}-\d{4})-/,
    // Box SKU patterns
    BOX_PREFIX: /^BX-/i,
    BOX_SUFFIX: /-BOX$/i,
};

// Cache configuration
const CACHE_CONFIG = {
    TTL: 5 * 60 * 1000, // 5 minutes
    ENABLED: true
};

// Fallback image for products without images
const FALLBACK_IMAGE = '/placeholder-product.svg';

// ============================================================================
// CACHE MANAGER
// ============================================================================

class ProductCacheManager {
    constructor() {
        this.cache = new Map();
        this.timestamps = new Map();
        this.ttl = CACHE_CONFIG.TTL;
    }

    set(key, value) {
        this.cache.set(key, value);
        this.timestamps.set(key, Date.now());
        console.log(`[ProductCache] Cached: ${key}`);
    }

    get(key) {
        if (!this.cache.has(key)) return null;
        
        const timestamp = this.timestamps.get(key);
        if (Date.now() - timestamp > this.ttl) {
            this.delete(key);
            return null;
        }
        
        return this.cache.get(key);
    }

    delete(key) {
        this.cache.delete(key);
        this.timestamps.delete(key);
        console.log(`[ProductCache] Cleared: ${key}`);
    }

    clear() {
        this.cache.clear();
        this.timestamps.clear();
        console.log(`[ProductCache] Cleared all cache`);
    }

    has(key) {
        if (!this.cache.has(key)) return false;
        const timestamp = this.timestamps.get(key);
        return Date.now() - timestamp <= this.ttl;
    }

    getStats() {
        return {
            entries: this.cache.size,
            ttl: this.ttl,
            oldestEntry: this.timestamps.size > 0 ? 
                Math.min(...Array.from(this.timestamps.values())) : null
        };
    }
}

// ============================================================================
// UNIFIED PRODUCT SERVICE
// ============================================================================

class UnifiedProductService {
    constructor() {
        this.cache = new ProductCacheManager();
        this.products = new Map();              // SKU → product object (from products_default)
        this.skuMetadata = new Map();           // SKU → metadata (from SkuMetadata)
        this.boxes = new Map();                 // SKU → box definition
        this.gtinMap = new Map();               // GTIN → SKU mapping
        this.listeners = new Set();             // Change listeners
        this.subscriptions = [];                // Firestore subscriptions
        this.isInitialized = false;
        this.currentTier = 'default';
    }

    // ========================================================================
    // INITIALIZATION & DATA LOADING
    // ========================================================================

    /**
     * Initialize the service - sets up Firestore listeners
     * @returns {Promise<void>}
     */
    async initialize() {
        if (this.isInitialized) {
            console.log('[UnifiedProductService] Already initialized');
            return;
        }

        console.log('[UnifiedProductService] Initializing...');

        try {
            // Load products from default tier
            await this.loadProductsFromFirestore(this.currentTier);
            
            // Load SKU metadata (reference data)
            await this.loadSKUMetadata();
            
            // Load box definitions
            await this.loadBoxDefinitions();
            
            // Load GTIN mappings
            await this.loadGTINMappings();
            
            this.isInitialized = true;
            console.log('[UnifiedProductService] Initialization complete', {
                products: this.products.size,
                skuMetadata: this.skuMetadata.size,
                boxes: this.boxes.size,
                gtinMappings: this.gtinMap.size
            });
        } catch (error) {
            console.error('[UnifiedProductService] Initialization failed:', error);
            throw error;
        }
    }

    /**
     * Load products from Firestore with real-time listener
     * @param {string} tier - Pricing tier (default, a, b, c)
     * @returns {Promise<Function>} Unsubscribe function
     */
    async loadProductsFromFirestore(tier = 'default') {
        const collectionName = `${PRODUCT_COLLECTIONS.PRODUCTS_PREFIX}${tier.toLowerCase()}`;
        
        console.log(`[UnifiedProductService] Loading products from: ${collectionName}`);

        return new Promise((resolve, reject) => {
            try {
                const unsubscribe = onSnapshot(
                    collection(db, collectionName),
                    (snapshot) => {
                        console.log(`[UnifiedProductService] Snapshot received from ${collectionName}:`, {
                            size: snapshot.size,
                            empty: snapshot.empty,
                            docs: snapshot.docs.length
                        });
                        
                        if (snapshot.empty) {
                            console.warn(`[UnifiedProductService] Collection ${collectionName} is EMPTY! No products found.`);
                        }
                        
                        snapshot.forEach((doc) => {
                            const data = doc.data();
                            
                            // Log first product for debugging
                            if (this.products.size === 0) {
                                console.log(`[UnifiedProductService] First product from ${collectionName}:`, {
                                    docId: doc.id,
                                    data: data,
                                    hasCode: !!(data.Code || data.code || data.sku),
                                    hasName: !!(data.Name || data.name)
                                });
                            }
                            const sku = data.Code || data.code || doc.id;
                            
                            // Normalize product data structure
                            const product = this.normalizeProduct(sku, data);
                            this.products.set(sku, product);
                        });
                        
                        console.log(`[UnifiedProductService] Loaded ${snapshot.size} products from ${collectionName}`);
                        this.notifyListeners();
                        resolve(unsubscribe);
                    },
                    (error) => {
                        console.error(`[UnifiedProductService] Error loading products:`, error);
                        reject(error);
                    }
                );

                this.subscriptions.push(unsubscribe);
            } catch (error) {
                console.error('[UnifiedProductService] Failed to set up product listener:', error);
                reject(error);
            }
        });
    }

    /**
     * Load SKU metadata from reference collection (real-time)
     * SkuMetadata = production definitions not in products_default
     * @returns {Promise<void>}
     */
    async loadSKUMetadata() {
        return new Promise((resolve, reject) => {
            try {
                const unsubscribe = onSnapshot(
                    collection(db, PRODUCT_COLLECTIONS.SKU_METADATA),
                    (snapshot) => {
                        this.skuMetadata.clear();
                        snapshot.forEach((doc) => {
                            const data = doc.data();
                            const sku = data.sku || doc.id;
                            this.skuMetadata.set(sku, {
                                sku,
                                packSize: data.packSize || '',
                                description: data.description || '',
                                category: data.category || '',
                                productKg: data.productKg || 0,
                                shippingKg: data.shippingKg || 0,
                                skuRoot: data.skuRoot || '',
                                isBox: data.isBox || false,
                                ...data
                            });
                        });
                        console.log(`[UnifiedProductService] Loaded ${this.skuMetadata.size} SKU metadata entries (real-time)`);
                        this.notifyListeners();
                        resolve(unsubscribe);
                    },
                    (error) => {
                        console.error('[UnifiedProductService] Error loading SKU metadata:', error);
                        reject(error);
                    }
                );
                this.subscriptions.push(unsubscribe);
            } catch (error) {
                console.error('[UnifiedProductService] Failed to set up SkuMetadata listener:', error);
                reject(error);
            }
        });
    }

    /**
     * Load box definitions from Firestore
     * @returns {Promise<void>}
     */
    async loadBoxDefinitions() {
        try {
            const snapshot = await getDocs(collection(db, PRODUCT_COLLECTIONS.BOX_DEFINITIONS));
            
            snapshot.forEach((doc) => {
                const data = doc.data();
                const sku = data.sku || doc.id;
                
                this.boxes.set(sku, {
                    sku,
                    name: data.name || sku,
                    items: data.items || [],
                    updatedAt: data.updatedAt,
                    updatedBy: data.updatedBy
                });
            });
            
            console.log(`[UnifiedProductService] Loaded ${this.boxes.size} box definitions`);
        } catch (error) {
            console.error('[UnifiedProductService] Error loading box definitions:', error);
            // Don't throw - boxes are optional
        }
    }

    /**
     * Load GTIN to SKU mappings from StandardItems
     * @returns {Promise<void>}
     */
    async loadGTINMappings() {
        try {
            const snapshot = await getDocs(collection(db, PRODUCT_COLLECTIONS.STANDARD_ITEMS));
            
            snapshot.forEach((doc) => {
                const data = doc.data();
                if (data.gtin13 && data.sku) {
                    this.gtinMap.set(data.gtin13, data.sku);
                }
            });
            
            console.log(`[UnifiedProductService] Loaded ${this.gtinMap.size} GTIN mappings`);
        } catch (error) {
            console.error('[UnifiedProductService] Error loading GTIN mappings:', error);
            // Don't throw - GTIN mappings are optional
        }
    }

    /**
     * Normalize product data to consistent structure
     * @param {string} sku - Product SKU
     * @param {Object} data - Raw Firestore data
     * @returns {Object} Normalized product object
     */
    normalizeProduct(sku, data) {
        return {
            sku,
            code: sku,
            name: data.Name || data.name || 'Unknown Product',
            packSize: data['Pack Size'] || data.packSize || '',
            price: data.Price || data.price || 0,
            category: data.Category || data.category || '',
            description: data.Description || data.description || '',
            imageUrl: data['Image URL'] || data.imageUrl || null,
            ImageURLs: data.ImageURLs || (data.imageUrl || data['Image URL'] ? [data.imageUrl || data['Image URL']] : []),
            stock: data.Stock || data.stock,
            minOrder: data['Min Order'] || data.minOrder || 1,
            active: data.active !== false,
            onSale: data.onSale || false,
            salePrice: data.salePrice || null,
            previousPrice: data.previousPrice || null,
            // Optional sale window (additive; honoured by productSaleUtils). Open-ended if absent.
            saleStartAt: data.saleStartAt || null,
            saleEndAt: data.saleEndAt || null,
            featured: data['Featured This week'] || data.featured || false,
            // Preserve original data
            _raw: data
        };
    }

    // ========================================================================
    // PRODUCT RETRIEVAL
    // ========================================================================

    /**
     * Get complete product object by SKU
     * Checks products_default first, then SkuMetadata (all production definitions)
     * @param {string} sku - Product SKU
     * @returns {Object|null} Product object or null
     */
    getProduct(sku) {
        if (!sku) return null;
        const fromProducts = this.products.get(sku);
        if (fromProducts) return fromProducts;
        const meta = this.skuMetadata.get(sku);
        if (meta) {
            return {
                sku: meta.sku,
                name: meta.description || meta.sku,
                packSize: meta.packSize || '—',
                category: meta.category || 'Other',
                ...meta
            };
        }
        return null;
    }

    /**
     * Get product name by SKU with graceful fallback
     * Falls back to SkuMetadata if not in products collection
     * @param {string} sku - Product SKU
     * @returns {string} Product name or SKU
     */
    getProductName(sku) {
        if (!sku) return 'Unknown Product';
        
        // Try products collection first
        const product = this.getProduct(sku);
        if (product && product.name && product.name !== 'Unknown Product') {
            return product.name;
        }
        
        // Fallback to SKU metadata
        const metadata = this.skuMetadata.get(sku);
        if (metadata && metadata.description) {
            return metadata.description;
        }
        
        // Last resort: return SKU itself
        return sku;
    }

    /**
     * Get product pack size by SKU
     * Falls back to SkuMetadata if not in products collection
     * @param {string} sku - Product SKU
     * @returns {string} Pack size or empty string
     */
    getProductPackSize(sku) {
        // Try products collection first
        const product = this.getProduct(sku);
        if (product && product.packSize) {
            return product.packSize;
        }
        
        // Fallback to SKU metadata
        const metadata = this.skuMetadata.get(sku);
        if (metadata && metadata.packSize) {
            return metadata.packSize;
        }
        
        return '';
    }

    /**
     * Get product category by SKU
     * Falls back to SkuMetadata if not in products collection
     * @param {string} sku - Product SKU
     * @returns {string} Category or empty string
     */
    getProductCategory(sku) {
        const forced = getOverriddenProductCategoryIfAny(sku);
        if (forced) return forced;

        // Try products collection first
        const product = this.getProduct(sku);
        if (product && product.category) {
            return product.category;
        }
        
        // Fallback to SKU metadata
        const metadata = this.skuMetadata.get(sku);
        if (metadata && metadata.category) {
            return metadata.category;
        }
        
        return '';
    }

    /**
     * Get product price by SKU
     * @param {string} sku - Product SKU
     * @returns {number} Price or 0
     */
    getProductPrice(sku) {
        const product = this.getProduct(sku);
        return product ? product.price : 0;
    }

    /**
     * Get all production definitions (alias for getAllProducts)
     * Use when you need complete product coverage from all sources
     * @returns {Array} Array of all products
     */
    getAllProductionDefinitions() {
        return this.getAllProducts();
    }

    /**
     * Get all production definitions as array
     * Combines products_default AND SkuMetadata for complete coverage.
     * Use this for Quick Slice, stock takes, and any tool that needs all defined products.
     * @returns {Array} Array of all products
     */
    getAllProducts() {
        const allProducts = new Map();
        
        // Start with products from products_default
        this.products.forEach((product, sku) => {
            allProducts.set(sku, product);
        });
        
        // Add products from SkuMetadata that aren't already in products_default
        // This ensures stock takes and other tools can see all defined products
        this.skuMetadata.forEach((metadata, sku) => {
            if (!allProducts.has(sku)) {
                // Create product object from metadata
                allProducts.set(sku, {
                    sku: sku,
                    name: metadata.description || sku,
                    packSize: metadata.packSize || '—',
                    category: metadata.category || 'Other',
                    description: metadata.description || '',
                    source: 'SkuMetadata', // Mark where it came from
                    active: true,
                    ...metadata
                });
            }
        });
        
        console.log(`[UnifiedProductService] getAllProducts() returning ${allProducts.size} products (${this.products.size} from products_default, ${allProducts.size - this.products.size} from SkuMetadata)`);
        
        return Array.from(allProducts.values());
    }

    /**
     * Get multiple products by SKU array
     * @param {Array<string>} skus - Array of SKUs
     * @returns {Array} Array of product objects (nulls filtered out)
     */
    getProductsBySKUs(skus) {
        if (!Array.isArray(skus)) return [];
        return skus.map(sku => this.getProduct(sku)).filter(Boolean);
    }

    /**
     * Check if a product exists
     * @param {string} sku - Product SKU
     * @returns {boolean} True if product exists
     */
    hasProduct(sku) {
        return this.products.has(sku);
    }

    /**
     * Get product count
     * @returns {number} Number of products
     */
    getProductCount() {
        return this.products.size;
    }

    // ========================================================================
    // SKU ROOT OPERATIONS
    // ========================================================================

    /**
     * Extract SKU root intelligently
     * Patterns:
     * - CH-NDSM-0080-SLC → CH-NDSM (2 segments before numeric)
     * - BA-STSM-0170-SLC → BA-STSM
     * - FS-CLAS-0700-PCE → FS-CLAS
     * 
     * @param {string} sku - Full SKU
     * @returns {string} SKU root or original SKU
     */
    getSKURoot(sku) {
        if (!sku) return '';
        
        const skuStr = String(sku).trim().toUpperCase();
        
        // Try two-segment pattern first (most common)
        const twoSegMatch = skuStr.match(SKU_PATTERNS.TWO_SEGMENT);
        if (twoSegMatch) {
            return twoSegMatch[1]; // e.g., "CH-NDSM"
        }
        
        // Try three-segment pattern
        const threeSegMatch = skuStr.match(SKU_PATTERNS.THREE_SEGMENT);
        if (threeSegMatch) {
            return threeSegMatch[1]; // e.g., "CH-NDSM-0080"
        }
        
        // Intelligent fallback: look for pattern before first numeric segment
        const parts = skuStr.split('-');
        const result = [];
        
        for (const part of parts) {
            // If we hit a numeric segment (like 0080, 0170), stop before it
            if (/^\d+$/.test(part)) {
                break;
            }
            result.push(part);
            
            // Stop at 2 segments for most cases
            if (result.length >= 2) {
                break;
            }
        }
        
        // Return the root if we found at least 2 parts, otherwise return original
        return result.length >= 2 ? result.join('-') : skuStr;
    }

    /**
     * Get all products with a specific SKU root
     * @param {string} root - SKU root (e.g., "CH-NDSM")
     * @returns {Array} Array of products with matching root
     */
    getProductsByRoot(root) {
        if (!root) return [];
        
        const rootUpper = root.toUpperCase();
        return this.getAllProducts().filter(product => {
            const productRoot = this.getSKURoot(product.sku);
            return productRoot === rootUpper;
        });
    }

    /**
     * Get all unique SKU roots from products
     * @returns {Array<string>} Array of unique SKU roots
     */
    getAllSKURoots() {
        const roots = new Set();
        this.getAllProducts().forEach(product => {
            const root = this.getSKURoot(product.sku);
            if (root) roots.add(root);
        });
        return Array.from(roots).sort();
    }

    // ========================================================================
    // BOX OPERATIONS
    // ========================================================================

    /**
     * Check if SKU is a box product
     * @param {string} sku - Product SKU
     * @returns {boolean} True if box
     */
    isBox(sku) {
        if (!sku) return false;
        
        const skuStr = String(sku);
        
        // Check if in box definitions
        if (this.boxes.has(sku)) return true;
        
        // Check SKU pattern
        return SKU_PATTERNS.BOX_PREFIX.test(skuStr) || SKU_PATTERNS.BOX_SUFFIX.test(skuStr);
    }

    /**
     * Get box definition and contents
     * @param {string} sku - Box SKU
     * @returns {Object|null} Box definition with items array
     */
    getBoxContents(sku) {
        if (!this.isBox(sku)) return null;
        return this.boxes.get(sku) || null;
    }

    /**
     * Expand items array, replacing box SKUs with their components
     * Handles nested boxes recursively (boxes within boxes)
     * @param {Array} items - Array of {sku, quantity} objects
     * @param {number} maxDepth - Maximum recursion depth to prevent infinite loops (default: 10)
     * @param {boolean} keepBoxes - If true, keeps box SKUs AND adds their components (default: true)
     * @returns {Array} Expanded array with boxes and/or their component products
     */
    expandBoxes(items, maxDepth = 10, keepBoxes = true) {
        if (!Array.isArray(items)) return [];
        if (maxDepth <= 0) {
            console.warn('[UnifiedProductService] Max recursion depth reached in expandBoxes');
            return items;
        }
        
        const expanded = [];
        let hasBoxes = false;
        
        for (const item of items) {
            const sku = item.sku || '';
            const quantity = Number(item.quantity) || 0;
            
            if (!sku || quantity <= 0) continue;
            
            // Check if it's a box
            const boxDef = this.getBoxContents(sku);
            
            if (boxDef && Array.isArray(boxDef.items)) {
                hasBoxes = true;
                
                // If keepBoxes is true, add the box itself to the list
                if (keepBoxes) {
                    expanded.push({
                        sku,
                        quantity,
                        isBox: true // Mark as box for UI purposes
                    });
                }
                
                // Add box components (these will be recursively expanded if they're also boxes)
                boxDef.items.forEach(component => {
                    if (component.sku && component.qty) {
                        expanded.push({
                            sku: component.sku,
                            quantity: quantity * Number(component.qty),
                            parentBox: sku // Track what box this came from
                        });
                    }
                });
            } else {
                // Not a box, add as-is
                expanded.push({
                    sku,
                    quantity
                });
            }
        }
        
        // If we found any boxes, recursively expand components (not the boxes themselves)
        if (hasBoxes) {
            // Separate boxes from components
            const boxes = expanded.filter(item => item.isBox);
            const components = expanded.filter(item => !item.isBox);
            
            // Recursively expand components (in case they're boxes too)
            const expandedComponents = this.expandBoxes(components, maxDepth - 1, keepBoxes);
            
            // Combine boxes with expanded components
            if (keepBoxes) {
                return [...boxes, ...expandedComponents];
            } else {
                return expandedComponents;
            }
        }
        
        // Consolidate duplicate SKUs
        const consolidated = new Map();
        for (const item of expanded) {
            const key = item.sku;
            const existing = consolidated.get(key);
            
            if (existing) {
                existing.quantity += item.quantity;
                // Merge parentBox info if exists
                if (item.parentBox && !existing.parentBox) {
                    existing.parentBox = item.parentBox;
                }
            } else {
                consolidated.set(key, { ...item });
            }
        }
        
        return Array.from(consolidated.values());
    }

    /**
     * Get all final components of a box, recursively expanding nested boxes
     * @param {string} boxSku - Box SKU to expand
     * @param {number} quantity - Quantity of the box
     * @returns {Array} Array of final component {sku, quantity} objects
     */
    getBoxFinalComponents(boxSku, quantity = 1) {
        if (!this.isBox(boxSku)) {
            return [{ sku: boxSku, quantity }];
        }
        
        const boxDef = this.getBoxContents(boxSku);
        if (!boxDef || !Array.isArray(boxDef.items)) {
            return [{ sku: boxSku, quantity }];
        }
        
        return this.expandBoxes([{ sku: boxSku, quantity }]);
    }

    /**
     * Get box hierarchy showing nesting levels
     * @param {string} boxSku - Box SKU
     * @param {number} depth - Current depth level
     * @returns {Object} Hierarchy tree with nested boxes
     */
    getBoxHierarchy(boxSku, depth = 0) {
        if (depth > 10) return null; // Prevent infinite recursion
        
        const boxDef = this.getBoxContents(boxSku);
        if (!boxDef) return null;
        
        return {
            sku: boxSku,
            name: boxDef.name,
            depth,
            items: (boxDef.items || []).map(component => {
                const isNestedBox = this.isBox(component.sku);
                
                return {
                    sku: component.sku,
                    quantity: component.qty,
                    name: this.getProductName(component.sku),
                    isBox: isNestedBox,
                    children: isNestedBox ? this.getBoxHierarchy(component.sku, depth + 1) : null
                };
            })
        };
    }

    /**
     * Get all box definitions
     * @returns {Array} Array of all box definitions
     */
    getAllBoxes() {
        return Array.from(this.boxes.values());
    }

    // ========================================================================
    // IMAGE OPERATIONS
    // ========================================================================

    /**
     * Get all product images
     * @param {string} sku - Product SKU
     * @returns {Array<string>} Array of image URLs
     */
    getProductImages(sku) {
        const product = this.getProduct(sku);
        if (!product) return [FALLBACK_IMAGE];
        
        // Return ImageURLs array if available
        if (Array.isArray(product.ImageURLs) && product.ImageURLs.length > 0) {
            return product.ImageURLs.filter(Boolean);
        }
        
        // Fallback to single imageUrl
        if (product.imageUrl) {
            return [product.imageUrl];
        }
        
        return [FALLBACK_IMAGE];
    }

    /**
     * Get primary product image
     * @param {string} sku - Product SKU
     * @returns {string} Primary image URL or fallback
     */
    getPrimaryImage(sku) {
        const images = this.getProductImages(sku);
        return images[0] || FALLBACK_IMAGE;
    }

    // ========================================================================
    // SEARCH & FILTER
    // ========================================================================

    /**
     * Search products by name or SKU
     * @param {string} query - Search query
     * @returns {Array} Array of matching products
     */
    searchProducts(query) {
        if (!query) return this.getAllProducts();
        
        const lowerQuery = query.toLowerCase();
        
        return this.getAllProducts().filter(product => {
            return (
                product.name.toLowerCase().includes(lowerQuery) ||
                product.sku.toLowerCase().includes(lowerQuery) ||
                (product.description && product.description.toLowerCase().includes(lowerQuery)) ||
                (product.category && product.category.toLowerCase().includes(lowerQuery))
            );
        });
    }

    /**
     * Get products by category
     * @param {string} category - Category name
     * @returns {Array} Array of products in category
     */
    getProductsByCategory(category) {
        if (!category) return [];
        
        const lowerCategory = category.toLowerCase();
        
        return this.getAllProducts().filter(product => 
            product.category && product.category.toLowerCase() === lowerCategory
        );
    }

    /**
     * Get all unique categories
     * @returns {Array<string>} Array of unique categories
     */
    getAllCategories() {
        const categories = new Set();
        this.getAllProducts().forEach(product => {
            if (product.category) categories.add(product.category);
        });
        return Array.from(categories).sort();
    }

    /**
     * Get active products only
     * @returns {Array} Array of active products
     */
    getActiveProducts() {
        return this.getAllProducts().filter(product => product.active);
    }

    // ========================================================================
    // GTIN OPERATIONS
    // ========================================================================

    /**
     * Resolve SKU from GTIN-13 barcode
     * @param {string} gtin13 - GTIN-13 barcode
     * @returns {string|null} Mapped SKU or null
     */
    async resolveSkuFromGtin(gtin13) {
        const code = String(gtin13 || '').trim();
        if (!/^\d{13}$/.test(code)) return null;
        
        // Check in-memory map first
        if (this.gtinMap.has(code)) {
            return this.gtinMap.get(code);
        }
        
        // Query Firestore if not in cache
        try {
            const snapshot = await getDocs(
                query(
                    collection(db, PRODUCT_COLLECTIONS.STANDARD_ITEMS),
                    where('gtin13', '==', code)
                )
            );
            
            if (!snapshot.empty) {
                const sku = snapshot.docs[0].data().sku;
                // Cache for future use
                this.gtinMap.set(code, sku);
                return sku;
            }
        } catch (error) {
            console.error('[UnifiedProductService] Error resolving GTIN:', error);
        }
        
        return null;
    }

    // ========================================================================
    // LISTENERS & SUBSCRIPTIONS
    // ========================================================================

    /**
     * Add a listener for product updates
     * @param {Function} callback - Callback function
     */
    addListener(callback) {
        this.listeners.add(callback);
        // Call immediately with current data
        if (typeof callback === 'function') {
            callback(this.getAllProducts());
        }
    }

    /**
     * Remove a listener
     * @param {Function} callback - Callback function
     */
    removeListener(callback) {
        this.listeners.delete(callback);
    }

    /**
     * Notify all listeners of changes
     */
    notifyListeners() {
        this.listeners.forEach(callback => {
            try {
                callback(this.getAllProducts());
            } catch (error) {
                console.error('[UnifiedProductService] Error in listener:', error);
            }
        });
    }

    /**
     * Clean up subscriptions
     */
    cleanup() {
        this.subscriptions.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        });
        this.subscriptions = [];
        this.listeners.clear();
        console.log('[UnifiedProductService] Cleaned up subscriptions');
    }

    // ========================================================================
    // CACHE MANAGEMENT
    // ========================================================================

    /**
     * Clear all caches
     */
    clearCache() {
        this.cache.clear();
        console.log('[UnifiedProductService] Cache cleared');
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache stats
     */
    getCacheStats() {
        return {
            ...this.cache.getStats(),
            products: this.products.size,
            skuMetadata: this.skuMetadata.size,
            boxes: this.boxes.size,
            gtinMappings: this.gtinMap.size,
            listeners: this.listeners.size
        };
    }

    /**
     * Refresh all data from Firestore
     */
    async refresh() {
        console.log('[UnifiedProductService] Refreshing data...');
        this.cleanup();
        this.products.clear();
        this.skuMetadata.clear();
        this.boxes.clear();
        this.gtinMap.clear();
        this.isInitialized = false;
        await this.initialize();
    }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

const unifiedProductService = new UnifiedProductService();

// Auto-initialize
unifiedProductService.initialize().catch(error => {
    console.error('[UnifiedProductService] Auto-initialization failed:', error);
});

export default unifiedProductService;

